import {
  RuleConditionDefinition,
  RuleActionDefinition,
  RuleCondition,
  RuleConditionBlock,
  RuleConditionItem,
  RuleAction,
  RuleCustomValue,
  AssignmentType,
} from '@/types';

// --- Hydration helpers ---

function hydrateCustomValues(
  savedCustomValues: RuleCustomValue[],
  definitionCustomValues: RuleCustomValue[],
): RuleCustomValue[] {
  return definitionCustomValues.map((defCv) => {
    const savedCv = savedCustomValues.find((s) => s.name === defCv.name);
    // Un boolean aggiunto alla definizione dopo il salvataggio vale false, come
    // in creazione: senza valore isCustomValueComplete terrebbe Salva disabilitato
    if (!savedCv) return defCv.type === 'boolean' ? { ...defCv, booleanValue: false } : { ...defCv };

    const merged: RuleCustomValue = { ...defCv, ...savedCv };

    switch (defCv.type) {
      case 'category':
        merged.value = savedCv.categoryValue ?? null;
        break;
      case 'tag':
        merged.value = savedCv.tagValue ?? null;
        break;
      case 'checklist':
        merged.value = savedCv.checklistValue ?? null;
        break;
      case 'template':
        merged.value = savedCv.templateValue ?? null;
        break;
      case 'user':
        if (defCv.multiple) {
          try {
            merged.value = typeof savedCv.value === 'string'
              ? JSON.parse(savedCv.value)
              : savedCv.value;
          } catch {
            merged.value = savedCv.value;
          }
        } else {
          merged.value = savedCv.userValue ?? null;
        }
        break;
      case 'group':
        if (defCv.multiple) {
          try {
            merged.value = typeof savedCv.value === 'string'
              ? JSON.parse(savedCv.value)
              : savedCv.value;
          } catch {
            merged.value = savedCv.value;
          }
        } else {
          merged.value = savedCv.groupValue ?? null;
        }
        break;
      case 'status':
      case 'activityStatus':
        try {
          merged.value = typeof savedCv.value === 'string'
            ? JSON.parse(savedCv.value)
            : savedCv.value;
        } catch {
          merged.value = savedCv.value;
        }
        break;
      case 'boolean':
        // No transform needed: booleanValue is used as-is
        break;
      default:
        // text, task, attachmentType, date, integer, float,
        // egridConnectionStatus, commissioningStatus, assignmentType, etc.
        // Keep value as-is
        break;
    }

    return merged;
  });
}

export function hydrateCondition(
  saved: RuleCondition,
  definitions: RuleConditionDefinition[],
): RuleCondition | null {
  const definition = definitions.find((d) => d.codename === saved.codename);
  if (!definition) return null;

  return {
    codename: definition.codename,
    name: definition.name,
    customValues: hydrateCustomValues(saved.customValues || [], definition.customValues || []),
  };
}

export function isBlock(item: RuleConditionItem): item is RuleConditionBlock {
  return 'block' in item;
}

export function hydrateConditionItems(
  savedItems: any[],
  definitions: RuleConditionDefinition[],
): RuleConditionItem[] {
  return savedItems.map((item) => {
    if (item && item.block) {
      return {
        block: item.block,
        conditions: hydrateConditionItems(item.conditions || [], definitions),
      } as RuleConditionBlock;
    }
    return hydrateCondition(item, definitions);
  }).filter((item): item is RuleConditionItem => item !== null);
}

export function hydrateAction(
  saved: RuleAction,
  definitions: RuleActionDefinition[],
): RuleAction | null {
  const definition = definitions.find((d) => d.codename === saved.codename);
  if (!definition) return null;

  return {
    codename: definition.codename,
    name: definition.name,
    order: saved.order,
    customValues: hydrateCustomValues(saved.customValues || [], definition.customValues || []),
  };
}

// --- Mittente delle azioni email ---

// Mittente proposto dall'editor: lo stesso di MAIL_FROM in production
// (backend/config/index.js), con il nome del portale
// (OwlCaveLabs/syndesia-l2c-support#46)
export const DEFAULT_RULE_SENDER = '"SYNDESK - Syndesia" <info@syndesia.eu>';

// Mette il mittente proposto nel campo `sender`, se l'azione ne ha uno.
// Con `onlyIfEmpty` lascia un valore gia' scritto.
export function applyDefaultSender(customValues: RuleCustomValue[], onlyIfEmpty: boolean): void {
  customValues.forEach((cv) => {
    if (cv.name !== 'sender' || cv.type !== 'text') return;
    if (onlyIfEmpty && cv.value) return;
    // eslint-disable-next-line no-param-reassign
    cv.value = DEFAULT_RULE_SENDER;
  });
}

// --- Validation ---

// Se un valore ha qualcosa da mostrare. Sta qui e non dentro
// `RuleCustomValueDisplay` perche' lo usa anche chi decide se disegnare la riga
// che conterrebbe quel componente: se le due regole divergessero, comparirebbero
// righe vuote (segnalazione del cliente OwlCaveLabs/syndesia-l2c-support#2).
export function isCustomValueDisplayable(cv: RuleCustomValue): boolean {
  if (cv.type === 'boolean') return !!cv.booleanValue;
  if (Array.isArray(cv.value)) return cv.value.length > 0;
  return !!cv.value;
}

export function isCustomValueComplete(cv: RuleCustomValue): boolean {
  // Un valore facoltativo e' completo anche vuoto: senza questo controllo
  // "Invia una notifica email ai nuovi assegnatari" non si salvava se non
  // scegliendo un gruppo e un utente da escludere (segnalazione del cliente
  // OwlCaveLabs/syndesia-l2c-support#20)
  if (cv.optional) return true;

  if (cv.type === 'boolean') {
    return cv.booleanValue !== undefined && cv.booleanValue !== null;
  }

  if (Array.isArray(cv.value)) {
    return cv.value.length > 0;
  }

  return !!cv.value;
}

// --- Enum option functions ---

export function getStatusOptions(): Array<{ value: string; text: string }> {
  return [
    { value: 'new', text: 'Nuova' },
    { value: 'open', text: 'In Lavorazione' },
    { value: 'resolved', text: 'Completata' },
    { value: 'closed', text: 'Chiusa' },
    { value: 'rejected', text: 'Respinta' },
    { value: 'cancelled', text: 'Annullata' },
  ];
}

export function getTaskOptions(): Array<{ value: string; text: string }> {
  return [
    { value: 'C', text: 'C' },
    { value: 'P', text: 'P' },
    { value: 'O', text: 'O' },
    { value: 'Cm', text: 'Cm' },
    { value: 'S', text: 'S' },
    { value: 'A', text: 'A' },
    { value: 'V', text: 'V' },
    { value: 'M', text: 'M' },
    { value: 'I', text: 'I' },
    { value: 'R', text: 'R' },
  ];
}

export function getActivityStatusOptions(): Array<{ value: string; text: string }> {
  return [
    { value: 'todo', text: 'Da fare' },
    { value: 'done', text: 'Fatto' },
    { value: 'waiting', text: 'In attesa' },
    { value: 'na', text: 'Non applicabile' },
  ];
}

export function getTaskStatusOptions(): Array<{ value: string; text: string }> {
  return [
    { value: 'completed', text: 'Completato (verde)' },
    { value: 'error', text: 'Fallito (rosso)' },
    { value: 'reset', text: 'Incompleto / reset' },
  ];
}

export function getTaskStatusLabel(value: string): string {
  const option = getTaskStatusOptions().find((o) => o.value === value);
  return option ? option.text : value;
}

export function getEgridConnectionStatusOptions(): Array<{ value: string; text: string }> {
  return [
    { value: '', text: "Data prevista per l'allacciamento da definire" },
    { value: 'suggested', text: 'Date proposte' },
    { value: 'not-confirmed', text: 'Data concordata (da confermare a portale)' },
    { value: 'no-confirmation-required', text: 'Data concordata (non necessaria conferma a portale)' },
    { value: 'confirmed', text: 'Data confermata' },
    { value: 'connected', text: 'Allaccio confermato' },
    { value: 'ko', text: 'Allaccio KO da riprogrammare' },
    { value: 'na', text: 'Allaccio non applicabile' },
  ];
}

export function getCommissioningStatusOptions(): Array<{ value: string; text: string }> {
  return [
    { value: 'suggested', text: 'Data proposta' },
    { value: 'confirmed', text: 'Data confermata' },
    { value: 'tested', text: 'Collaudo confermato' },
  ];
}

type AttachmentTypeOption = { value: string; text: string };

// Lista completa dei tipi di allegato non-deprecati, allineata a
// `attachmentsDetails` in backend/models/attachment.js (single source of truth).
// Quando si aggiunge un nuovo tipo nel backend, replicarlo qui mantenendo lo
// stesso `value` e usando `description` come `text`. Vedi docs/attachments.md.
const ALL_ATTACHMENT_TYPES: ReadonlyArray<AttachmentTypeOption> = [
  { value: 'richiestaPreventivo', text: 'Richiesta di preventivo' },
  { value: 'ordine', text: 'Ordine' },
  { value: 'preventivo', text: 'Preventivo' },
  { value: 'preventivoRevisionato', text: 'Preventivo revisionato' },
  { value: 'ordineRevisionato', text: 'Ordine revisionato' },
  { value: 'reportEcobonusCheck', text: 'Report fattibilità 110%' },

  { value: 'visuraCatastale', text: 'Visura catastale' },
  { value: 'planimetriaCatastale', text: 'Planimetria catastale' },
  { value: 'ultimaBollettaElettrica', text: 'Ultima bolletta energia elettrica' },
  { value: 'documentiIdentita', text: "Carta d'identità e tessera sanitaria" },
  { value: 'autodichiarazioneCliente', text: 'Autodichiarazione cliente' },
  { value: 'titoloAutorizzativo', text: 'Titolo autorizzativo' },
  { value: 'unifilare', text: 'Unifilare' },

  { value: 'sopralluogoTecnico', text: 'Modulo per il sopralluogo / Lettera di incarico' },
  { value: 'esitoSopralluogoTecnico', text: 'Esito incarico tecnico' },
  { value: 'fotoSopralluogoTecnico', text: 'Foto del sopralluogo' },
  { value: 'extraSopralluogoTecnico', text: 'Preventivo con gli extra-costi' },

  { value: 'progettoElettrico', text: "Progetto dell'impianto elettrico" },
  { value: 'progettoTermico', text: "Progetto dell'impianto termico" },
  { value: 'pratiche', text: 'Allegati progetto e pratiche' },
  { value: 'computoMetrico', text: 'Computo metrico' },
  { value: 'praticaEnea', text: 'ENEA/GSE/Altro' },
  { value: 'praticaDogane', text: 'Dogane' },

  { value: 'confUrbCat', text: 'Relazione di conformità urbanistico catastale' },
  { value: 'modOutFattEner', text: 'Modulo output fattibilità energetica' },
  { value: 'apePre', text: 'APE Pre/APE Pre Convenzionale' },
  { value: 'apePostSimul', text: 'APE Post simulato in bozza' },

  { value: 'allegatiInstallazione', text: "Allegati per l'installazione" },
  { value: 'ddt', text: 'Documento di trasporto' },
  { value: 'inverter', text: 'Inverter' },
  { value: 'batteriaAC', text: 'Batteria lato AC' },
  { value: 'spiEsterna', text: 'SPI esterna (se prevista)' },

  { value: 'dicoTermica', text: 'DICO termica (dichiarazione di conformità)' },
  { value: 'dicoVisuraCamerale', text: 'DICO e Visura camerale' },
  { value: 'verbaleAllaccio', text: 'Verbale di allaccio' },
  { value: 'registrazioneCriter', text: 'Registrazione criter' },
  { value: 'fGas', text: 'Comunicazione F-Gas' },

  { value: 'fotoImpianto', text: 'Foto impianto realizzato' },
  { value: 'fotoDG', text: 'Foto DG' },
  { value: 'fotoInverter', text: 'Foto etichette inverter' },
  { value: 'fotoBatteria', text: 'Foto etichette batteria' },

  { value: 'endOfWork', text: 'Documentazione fine lavori' },
  { value: 'altri', text: 'Altri allegati' },
];

// Tipi con "stato attività" (todo / done / waiting / na). Allineata a
// `Attachment.ACTIVITY_STATUS_ATTACHMENT_TYPES` (backend/models/attachment.js),
// escludendo i deprecati. Lista derivata dalla UI: solo i tipi pilotati da
// AttachmentWithStatus (dialog connection / TaskCm / TaskR / EditMUP*) o da
// BoxStatus in TaskV/TaskR. Usata da condition activity.status.{changed,is} e
// action activity.status.set.
const ACTIVITY_STATUS_ATTACHMENT_TYPES = new Set([
  'titoloAutorizzativo', 'endOfWork',
  'progettoElettrico', 'progettoTermico', 'computoMetrico',
  'dicoTermica', 'fGas', 'registrazioneCriter',
  'fotoImpianto', 'confUrbCat', 'modOutFattEner', 'apePre', 'apePostSimul',
  'verbaleAllaccio', 'fotoDG', 'fotoInverter', 'fotoBatteria',
  'visuraCatastale', 'planimetriaCatastale', 'ultimaBollettaElettrica',
  'documentiIdentita', 'unifilare', 'dicoVisuraCamerale', 'inverter',
  'batteriaAC', 'spiEsterna', 'praticaEnea', 'praticaDogane',
  'autodichiarazioneCliente',
]);

// Label dei tipi deprecati: non offerti nelle <v-select> ma usati per
// rendere leggibili regole legacy che ancora referenziano questi valori.
const DEPRECATED_ATTACHMENT_LABELS: Record<string, string> = {
  dicoElettrica: 'DICO elettrica (deprecato)',
  visura: 'Visura camerale (deprecato)',
  autotest: 'Autotest inverter/batteria (deprecato)',
};

export function getAttachmentTypeOptions(): AttachmentTypeOption[] {
  return [...ALL_ATTACHMENT_TYPES];
}

export function getActivityStatusAttachmentTypeOptions(): AttachmentTypeOption[] {
  return ALL_ATTACHMENT_TYPES.filter((o) => ACTIVITY_STATUS_ATTACHMENT_TYPES.has(o.value));
}

export function getAttachmentTypeLabel(value: string): string {
  const option = ALL_ATTACHMENT_TYPES.find((o) => o.value === value);
  if (option) return option.text;
  return DEPRECATED_ATTACHMENT_LABELS[value] || value;
}

// I tipi arrivano dal backend (store `getAssignmentTypes`): un elenco scritto
// qui restava indietro a ogni tipo nuovo (OwlCaveLabs/syndesia-l2c-support#32).
export function getAssignmentTypeOptions(
  types: AssignmentType[],
): Array<{ value: string; text: string }> {
  return types.map((t) => ({ value: t.id, text: t.ruleLabel || t.label }));
}

// --- Label lookup helpers ---

function findLabel(
  options: Array<{ value: string; text: string }>,
  value: string,
): string {
  const option = options.find((o) => o.value === value);
  return option ? option.text : value;
}

function formatLabels(
  options: Array<{ value: string; text: string }>,
  values: string[],
): string {
  if (!values || values.length === 0) return '';
  const labels = values.map((v) => findLabel(options, v));
  return `(${labels.join(', ')})`;
}

export function getStatusLabel(value: string): string {
  return findLabel(getStatusOptions(), value);
}

export function getStatusLabels(values: string[]): string {
  return formatLabels(getStatusOptions(), values);
}

export function getActivityStatusLabels(values: string[]): string {
  return formatLabels(getActivityStatusOptions(), values);
}

export function getCommissioningStatusLabel(value: string): string {
  return findLabel(getCommissioningStatusOptions(), value);
}

export function getEgridConnectionStatusLabel(value: string): string {
  return findLabel(getEgridConnectionStatusOptions(), value);
}
